import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Typography,
  TypographyVariants,
  TypographyElements,
} from '@carboledger/ui';
import { LoadingOverlay } from '@/components/feedback';
import { ROUTES } from '@/app/routes';
import { AuthLayout } from '../components/AuthLayout';
import { SignupForm } from '../components/SignupForm';
import {
  useVerifyInvitationQuery,
  useCompleteRegistrationMutation,
  useGetOauthRedirectUrlMutation,
} from '../api/authApi';
import styles from './SignupPage.module.scss';
import { useTranslations } from '@/hooks';

const OAUTH_STATE_STORAGE_KEY = 'oauth_state';

// The invitation-acceptance screen.
export const SignupPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();

  const handleGoToLogin = useCallback(() => {
    navigate(ROUTES.login);
  }, [navigate]);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [registered, setRegistered] = useState(false);
  const [isOauthLoading, setIsOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState(null);

  const { isLoading: isVerifying, error: verifyError } = useVerifyInvitationQuery(token, {
    skip: !token,
  });
  const [completeRegistration, { isLoading: isSubmitting, error: submitError }] =
    useCompleteRegistrationMutation();
  const [getOauthRedirectUrl] = useGetOauthRedirectUrlMutation();

  const handleSubmit = async (values) => {
    const result = await completeRegistration({ token, ...values });
    if (result.error) return;
    setRegistered(true);
  };

  const handleSocialSignup = async (provider) => {
    setOauthError(null);
    setIsOauthLoading(true);
    const result = await getOauthRedirectUrl({ provider, urlType: 'REGISTER' });
    if (result.error) {
      setIsOauthLoading(false);
      setOauthError(result.error);
      return;
    }
    localStorage.setItem(OAUTH_STATE_STORAGE_KEY, result.data.state);
    window.location.href = result.data.redirectUrl;
  };

  if (!token) {
    return (
      <AuthLayout>
        <Alert
          variant={AlertVariants.danger}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
          className={styles.alert}
        >
          {t('AUTH_SIGNUP_MISSING_TOKEN')}
        </Alert>
      </AuthLayout>
    );
  }

  if (isOauthLoading) {
    return (
      <AuthLayout>
        <LoadingOverlay label={t('AUTH_REDIRECTING')} />
      </AuthLayout>
    );
  }

  if (isVerifying) {
    return (
      <AuthLayout>
        <LoadingOverlay label={t('AUTH_CHECKING_INVITATION')} />
      </AuthLayout>
    );
  }

  if (verifyError) {
    return (
      <AuthLayout>
        <Alert
          variant={AlertVariants.danger}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
          className={styles.alert}
        >
          {verifyError.data?.message ?? t('AUTH_INVITATION_INVALID')}
        </Alert>
      </AuthLayout>
    );
  }

  if (registered) {
    return (
      <AuthLayout>
        <div className={styles.success}>
          <Typography variant={TypographyVariants.h6} as={TypographyElements.h1}>
            {t('AUTH_SIGNUP_DONE_TITLE')}
          </Typography>
          <Alert
            variant={AlertVariants.success}
            appearance={AlertAppearances.accent}
            orientation={AlertOrientations.horizontal}
            className={styles.alert}
          >
            {t('AUTH_SIGNUP_DONE_BODY')}
          </Alert>
          <Button fullWidth size={ButtonSizes.sm} onClick={handleGoToLogin}>
            {t('AUTH_GO_TO_LOGIN')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <SignupForm
        onSubmit={handleSubmit}
        isLoading={isSubmitting}
        error={submitError ?? oauthError}
        onSocialSignup={handleSocialSignup}
      />
    </AuthLayout>
  );
};
